import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight,
  ChevronRight,
  Users,
  Layers,
  DollarSign,
  TrendingDown,
  Zap,
  CheckCircle,
  BarChart3,
  FileText,
  MessageSquare,
  Workflow,
  Shield,
  Menu,
  X,
  Clock,
  Target,
  ArrowUpRight,
  Sparkles,
  Calendar,
  ChevronDown,
  Play,
  ArrowUp,
  Smartphone
} from "lucide-react";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import AdminDashboard from "@/pages/AdminDashboard";

// Analytics tracking hook
const usePageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Generate or retrieve visitor ID
    let visitorId = localStorage.getItem('visitor_id');
    if (!visitorId) {
      visitorId = 'v_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('visitor_id', visitorId);
    }
    
    // Track page visit
    const trackVisit = async () => {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page_url: location.pathname,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            visitor_id: visitorId
          })
        });
      } catch (e) {
        // Silently fail - don't break the site if tracking fails
        console.debug('Tracking failed:', e);
      }
    };
    
    trackVisit();
  }, [location.pathname]);
};

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg shadow-red-500/30 flex items-center justify-center transition-colors"
          data-testid="back-to-top-btn"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// Cal.com booking handler
const openCalBooking = () => {
  if (window.Cal && window.Cal.ns && window.Cal.ns["30min"]) {
    window.Cal.ns["30min"]("ui", {"hideEventTypeDetails": false, "layout": "month_view"});
    window.Cal.ns["30min"]("modal", { calLink: "mdawson40/30min" });
  }
};

// Navigation
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };
  
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/10' : 'bg-transparent'}`}>
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-8 h-8" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M50 5L93.3 27.5V72.5L50 95L6.7 72.5V27.5L50 5Z" fill="#DC2626"/>
            <path d="M35 35H50C55.5 35 60 39.5 60 45C60 50.5 55.5 55 50 55H42V65H35V35ZM42 48H48C50.2 48 52 46.2 52 44C52 41.8 50.2 40 48 40H42V48Z" fill="white"/>
            <path d="M50 55L65 65H55L42 55H50Z" fill="white"/>
          </svg>
          <span className="font-display font-bold text-lg tracking-tight text-white">Build with Redline</span>
        </div>
        
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollTo('mission')} className="text-sm font-medium text-gray-400 hover:text-white transition-colors">Mission</button>
          <button onClick={() => scrollTo('process')} className="text-sm font-medium text-gray-400 hover:text-white transition-colors">Process</button>
          <button 
            onClick={openCalBooking}
            className="text-sm font-medium text-black bg-white px-5 py-2 rounded-full hover:bg-gray-200 transition-all"
            data-testid="nav-cta"
          >
            Book a Call
          </button>
        </div>

        <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-black border-t border-white/10 px-6 py-4 space-y-3">
          <button onClick={() => scrollTo('mission')} className="block text-gray-400 hover:text-white">Mission</button>
          <button onClick={() => scrollTo('process')} className="block text-gray-400 hover:text-white">Process</button>
          <button onClick={() => scrollTo('contact')} className="block text-gray-400 hover:text-white">Contact</button>
          <button 
            onClick={openCalBooking}
            className="block w-full text-black bg-white py-2.5 rounded-full font-medium cursor-pointer"
            data-testid="mobile-book-call-btn"
          >
            Book a Call
          </button>
        </div>
      )}
    </nav>
  );
};

// Hero
const Hero = () => {
  const [prompt, setPrompt] = useState('');
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  
  // Animated placeholder examples
  const placeholderExamples = [
    "A customer portal where clients can view invoices and track projects...",
    "An AI chatbot that handles customer support 24/7...",
    "A CRM replacement that our whole sales team actually wants to use...",
    "An automated invoicing system that saves us 15 hours a week...",
    "A scheduling tool that syncs with all our team calendars...",
    "A custom dashboard that pulls data from all our tools...",
  ];
  
  useEffect(() => {
    if (prompt) return; // Don't animate if user has typed something
    
    let exampleIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timeoutId;
    
    const typeEffect = () => {
      const currentExample = placeholderExamples[exampleIndex];
      
      if (!isDeleting) {
        // Typing
        setPlaceholderText(currentExample.substring(0, charIndex + 1));
        charIndex++;
        
        if (charIndex === currentExample.length) {
          // Pause at end before deleting
          isDeleting = true;
          timeoutId = setTimeout(typeEffect, 2000);
          return;
        }
        timeoutId = setTimeout(typeEffect, 50);
      } else {
        // Deleting
        setPlaceholderText(currentExample.substring(0, charIndex - 1));
        charIndex--;
        
        if (charIndex === 0) {
          isDeleting = false;
          exampleIndex = (exampleIndex + 1) % placeholderExamples.length;
          timeoutId = setTimeout(typeEffect, 500);
          return;
        }
        timeoutId = setTimeout(typeEffect, 30);
      }
    };
    
    timeoutId = setTimeout(typeEffect, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [prompt]);
  
  const templates = [
    { 
      name: "Invoice Automation", 
      icon: FileText,
      prompt: "I need an invoice automation system that can scan incoming invoices, extract key data (vendor, amount, due date), match them to purchase orders, route for approval, and sync with our accounting software. We process about 500+ invoices monthly."
    },
    { 
      name: "CRM Replacement", 
      icon: Users,
      prompt: "I want to replace our current CRM (Salesforce/HubSpot) with a custom solution. We need contact management, deal pipeline tracking, email integration, task reminders, and custom reporting. Our sales team is 15 people."
    },
    { 
      name: "Customer Onboarding", 
      icon: Workflow,
      prompt: "I need a customer onboarding workflow system that collects client information, sends welcome emails, assigns tasks to our team, tracks onboarding progress, and integrates with our existing tools. We onboard 20-30 new clients monthly."
    },
    { 
      name: "AI Support Bot", 
      icon: MessageSquare,
      prompt: "I want an AI-powered customer support bot that can answer common questions, search our knowledge base, escalate complex issues to humans, and work 24/7 on our website. We get about 200 support tickets per week."
    },
    { 
      name: "Quote Generator", 
      icon: DollarSign,
      prompt: "I need a quote/proposal generator that lets our sales team quickly build professional quotes, customize pricing tiers, add terms and conditions, get e-signatures, and track quote status. We send 50+ quotes monthly."
    },
    { 
      name: "Order Management", 
      icon: Layers,
      prompt: "I want an order management system that syncs orders from multiple sales channels (Shopify, Amazon, wholesale), tracks inventory, generates shipping labels, and provides real-time order status updates to customers."
    },
    { 
      name: "Security Monitoring", 
      icon: Shield,
      prompt: "I need a security monitoring dashboard that aggregates logs from our systems, detects anomalies, sends real-time alerts, and generates compliance reports. We have about 50 employees and handle sensitive customer data."
    },
    { 
      name: "Data Cleanup", 
      icon: BarChart3,
      prompt: "I want a data cleanup and enrichment tool that can deduplicate our customer database, standardize addresses, validate emails, merge duplicate records, and enrich contacts with missing information."
    },
    { 
      name: "Pipeline Dashboard", 
      icon: TrendingDown,
      prompt: "I need a real-time pipeline dashboard that pulls data from multiple sources, shows key metrics (revenue, leads, conversion rates), sends automated alerts, and lets me drill down into specific deals or time periods."
    },
    { 
      name: "Scheduling System", 
      icon: Calendar,
      prompt: "I want a scheduling system for our service business that lets customers book appointments online, syncs with team calendars, sends reminders, handles rescheduling, and prevents double-bookings. We have 10 team members."
    },
    { 
      name: "Enterprise Data Integration", 
      icon: Layers,
      prompt: "I need an enterprise data integration platform that connects our ERP, CRM, and HR systems, syncs data in real-time, handles data transformations, and provides a unified view across all departments. We have 200+ employees across multiple locations."
    },
    { 
      name: "Compliance & Reporting", 
      icon: Shield,
      prompt: "I want a compliance management system that tracks regulatory requirements (SOC 2, HIPAA, GDPR), automates audit trails, generates compliance reports, and sends alerts for policy violations. We're in a heavily regulated industry."
    },
    { 
      name: "Vendor Management", 
      icon: Users,
      prompt: "I need a vendor management portal where we can track contracts, monitor performance, manage approvals, handle renewals, and maintain compliance documentation for our 100+ vendors."
    },
    { 
      name: "Employee Portal", 
      icon: Workflow,
      prompt: "I want an internal employee portal with self-service HR functions, PTO requests, expense submissions, company announcements, document signing, and integrations with our payroll and benefits systems. We have 150 employees."
    },
    { 
      name: "Mobile App", 
      icon: Smartphone,
      prompt: "I need a custom mobile app for iOS and Android that lets our customers place orders, track deliveries, view their account history, and receive push notifications. We want it branded to our company with offline capabilities."
    },
  ];
  
  const selectTemplate = (template) => {
    setPrompt(template.prompt);
  };

  const visibleTemplates = showAllTemplates ? templates : templates.slice(0, 4);

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden min-h-screen flex items-center bg-black">
      {/* Background - cleaner, darker */}
      <div className="absolute inset-0 bg-black"></div>
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-red-500/10 via-red-500/5 to-transparent rounded-full blur-3xl"></div>
      
      <div className="relative max-w-5xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* Bold headline */}
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.1] mb-8 tracking-tight">
            <span className="text-white">Software built to fit.</span>
            <br />
            <span className="bg-gradient-to-r from-red-500 via-red-400 to-orange-400 bg-clip-text text-transparent">Yours to keep.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto mb-10 font-light">
            We build and automate the tools your business needs.
            <br />
            <span className="text-gray-300">We code it. You own it forever.</span>
          </p>
          
          {/* Text Entry Box */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative group">
              {/* Subtle glow on hover */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-white/10 to-white/5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative bg-neutral-900/80 backdrop-blur-sm rounded-2xl border border-white/10 p-4 shadow-2xl">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={placeholderText || "Describe what you want to build..."}
                  className="w-full bg-neutral-950/50 text-white placeholder-gray-500 px-5 py-4 rounded-xl border border-white/5 focus:outline-none focus:border-white/20 transition-all text-base resize-none h-32"
                  data-testid="hero-prompt-input"
                />
                <div className="flex justify-between items-center mt-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openCalBooking}
                    className="bg-white text-black px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center gap-2"
                    data-testid="hero-build-btn"
                  >
                    Build It
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openCalBooking}
                    className="text-gray-400 hover:text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2"
                  >
                    or Book a Call
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Templates */}
          <div className="max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-gray-600 mb-4">Popular requests</p>
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {visibleTemplates.map((template, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  onClick={() => selectTemplate(template)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-gray-400 hover:text-white transition-all"
                  data-testid={`template-${i}`}
                >
                  <template.icon className="w-4 h-4 text-red-400" />
                  {template.name}
                </motion.button>
              ))}
            </div>
            
            {!showAllTemplates && (
              <button
                onClick={() => setShowAllTemplates(true)}
                className="text-sm text-red-400 hover:text-red-300 transition-colors"
              >
                Show all {templates.length} templates
              </button>
            )}
            {showAllTemplates && (
              <button
                onClick={() => setShowAllTemplates(false)}
                className="text-sm text-gray-500 hover:text-gray-400 transition-colors"
              >
                Show less
              </button>
            )}
          </div>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-600 animate-bounce">
        <ChevronDown className="w-6 h-6" />
      </div>
    </section>
  );
};

// Stats bar
const StatsBar = () => (
  <section className="py-6 sm:py-10 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-3 sm:gap-4 md:gap-6">
        {[
          { value: "40%", label: "Cost Reduction", icon: TrendingDown },
          { value: "<12mo", label: "ROI Timeline", icon: Clock },
          { value: "100%", label: "Code Ownership", icon: Shield },
          { value: "$0", label: "Per-User Fees", icon: DollarSign }
        ].map((stat, i) => (
          <motion.div 
            key={i} 
            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
            </div>
            <div>
              <div className="font-display text-lg sm:text-xl md:text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-[10px] sm:text-xs text-gray-400">{stat.label}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Trusted By - REMOVED (was fake logos)

// Video Section
const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Replace this with your actual video URL (YouTube, Vimeo, or direct MP4)
  const videoUrl = null; // e.g., "https://www.youtube.com/embed/YOUR_VIDEO_ID" or "/videos/marketing.mp4"
  
  return (
    <section className="py-16 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 bg-red-100 text-red-600 text-sm font-medium rounded-full mb-4">See It In Action</span>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            How we transform your business
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Watch how we replace expensive SaaS subscriptions with custom solutions you own forever.
          </p>
        </div>
        
        <div className="relative aspect-video bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-2xl">
          {videoUrl && isPlaying ? (
            // Actual video player
            <iframe
              src={videoUrl}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            // Placeholder with play button
            <>
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 border border-white/50 rounded-full"></div>
                <div className="absolute bottom-10 right-10 w-48 h-48 border border-white/50 rounded-full"></div>
                <div className="absolute top-1/2 left-1/4 w-24 h-24 border border-white/50 rounded-full"></div>
              </div>
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              
              {/* Center content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
                <motion.button
                  onClick={() => videoUrl && setIsPlaying(true)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-16 h-16 sm:w-20 sm:h-20 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30 ${videoUrl ? 'cursor-pointer hover:bg-red-600' : 'cursor-default'}`}
                  data-testid="video-play-btn"
                >
                  <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white ml-1" fill="white" />
                </motion.button>
                <p className="mt-3 sm:mt-4 text-white/90 text-xs sm:text-sm font-medium">
                  {videoUrl ? 'Click to play' : 'Video coming soon'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// Tech Stack - Compact
const TechStack = () => {
  const techs = [
    { name: "React", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
    { name: "Angular", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg" },
    { name: "TypeScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" },
    { name: "Python", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" },
    { name: "Rust", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rust/rust-original.svg" },
    { name: "Node.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
    { name: "MongoDB", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" },
    { name: "PostgreSQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg" },
    { name: "AWS", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg" },
    { name: "Docker", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" },
    { name: "TensorFlow", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg" },
    { name: "Tailwind", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg" }
  ];

  return (
    <section className="py-12 px-6 bg-gray-900">
      <div className="max-w-6xl mx-auto">
        <p className="text-center text-sm text-gray-500 mb-6">Built with enterprise-grade technology</p>
        <div className="flex flex-wrap justify-center items-center gap-6">
          {techs.map((tech, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
              <img src={tech.icon} alt={tech.name} className="w-5 h-5" onError={(e) => e.target.style.display='none'} />
              <span className="text-sm text-gray-400">{tech.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Founders Section - simplified
const Founders = () => {
  return (
    <section className="py-24 px-6 bg-neutral-950">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Leadership</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white">Meet the founder</h2>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row items-center gap-8 max-w-2xl mx-auto"
        >
          <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_06f8874f-6210-42ab-a5d4-e65379100a3e/artifacts/vvuokksd_1689733819369%20%281%29.jpeg" 
              alt="Michael Dawson"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="text-center md:text-left">
            <h3 className="font-display text-2xl font-bold text-white">Michael Dawson</h3>
            <span className="text-red-400 text-sm font-medium">CEO & Founder</span>
            <p className="text-gray-400 mt-3 leading-relaxed">
              Strategic product leader with deep expertise in enterprise fintech and SaaS. Over a decade transforming businesses through cutting-edge software across North America, Europe, and Asia Pacific.
            </p>
            <a 
              href="https://www.linkedin.com/in/mikedawson40/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sm text-gray-400 hover:text-white transition-colors"
            >
              LinkedIn
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section - Compact
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    { 
      q: "Wait, why is there still a monthly fee?", 
      a: "Think of it like this: A SaaS subscription is rent—you pay forever, own nothing, and prices go up every year. Our Support Fee is maintenance on an asset you own. It covers hosting, security updates, and tuning as you grow. The key difference? We don't charge per-user. When you hire more people, your bill doesn't go up. We help you scale; we don't tax you for it.",
      highlight: true
    },
    { 
      q: "What if I stop paying the support fee?", 
      a: "You keep everything. The code, the data, the entire system—it's yours. SaaS locks you out the moment you stop paying. With us, you could take your code to any developer, host it yourself, or just pause support until you need us again. No hostage situations.",
      highlight: true
    },
    { 
      q: "How is this different from just using Salesforce, HubSpot, etc.?", 
      a: "Those tools charge you per-user and give you 100 features when you need 10. You spend hours on workarounds. We build exactly what you need—nothing more, nothing less. And when you grow from 5 to 50 employees, your Redline bill stays the same. Try that with Salesforce."
    },
    { 
      q: "How long does a typical project take?", 
      a: "2-8 weeks depending on complexity. Single workflow: 2-3 weeks. Full platform: 6-8 weeks. We'll give you a clear timeline after our discovery call." 
    },
    { 
      q: "Do I really own the code?", 
      a: "Yes, 100%. Full ownership of all source code, documentation, and assets. No licensing fees, no vendor lock-in. It's yours forever." 
    },
    { 
      q: "Can you integrate with our existing systems?", 
      a: "Absolutely. We specialize in integrations—CRMs, ERPs, databases, APIs, and legacy systems. If it has an API, we can connect to it." 
    },
    { 
      q: "What's included in the initial investment?", 
      a: "Everything: discovery, design, development, testing, deployment, training, and documentation. Plus a warranty period for bug fixes. No hidden costs." 
    }
  ];

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-display text-2xl font-bold text-gray-900 text-center mb-2">Frequently Asked Questions</h2>
        <p className="text-center text-gray-500 text-sm mb-8">The questions everyone asks (especially about pricing)</p>
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className={`bg-white rounded-xl border overflow-hidden ${faq.highlight ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-200'}`}>
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className={`font-medium text-sm ${faq.highlight ? 'text-red-600' : 'text-gray-900'}`}>{faq.q}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-5 pb-4">
                  <p className="text-gray-600 text-sm">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Project (Buscar) - Collapsible
const FeaturedProject = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="py-12 px-6 bg-white border-b border-gray-100">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl hover:from-gray-800 hover:to-gray-700 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">B</span>
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold">Featured: Buscar</span>
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Live</span>
              </div>
              <p className="text-gray-400 text-sm">AI-powered event discovery app for Web, iOS & Android</p>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 overflow-hidden"
          >
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-1">
                <h3 className="font-display text-2xl font-bold text-white mb-3">Buscar</h3>
                <p className="text-gray-300 mb-4">
                  A full-stack consumer app with AI voice search, personalized recommendations, and real-time event data. Built for web, iOS, and Android with a shared codebase.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {["React Native", "AI Voice Search", "Real-time Data", "iOS", "Android", "Web"].map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-white/10 text-gray-300 text-sm rounded-full">{tag}</span>
                  ))}
                </div>
                <a 
                  href="https://buscar.world" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors"
                >
                  Visit buscar.world
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
              <div className="w-48 h-80 bg-gray-700 rounded-[2rem] border-4 border-gray-600 overflow-hidden flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-2xl font-bold">BUSCAR</div>
                  <div className="text-xs opacity-60">Event Discovery</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// Mission Briefing - provocative positioning
const MissionBriefing = () => {
  return (
    <section id="mission" className="py-24 px-6 bg-black border-t border-white/5">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs uppercase tracking-widest text-gray-600 mb-6 text-center">Mission Briefing</p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
            You're renting software<br />
            <span className="text-gray-500">that should be yours.</span>
          </h2>
          
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            Every month you pay for SaaS tools and apps that charge per-user 
            and give you 100 features when you need 10. You work around their limitations 
            instead of building what actually fits.
          </p>
          
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 max-w-2xl mx-auto">
            <p className="text-white text-lg md:text-xl font-medium mb-4">
              We build what fits. You keep it forever.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-400" />
                Web apps & mobile
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-400" />
                Any API or database
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-400" />
                Full ownership, no fees
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Results - Before → After transformation style
const CaseStudies = () => {
  const transformations = [
    { before: "3 days", after: "2 hours", label: "Customer Onboarding" },
    { before: "Manual", after: "60% auto", label: "Support Tickets" },
    { before: "$8k/mo", after: "$0/mo", label: "SaaS Fees" },
    { before: "15 hrs/wk", after: "0 hrs", label: "Invoice Processing" },
    { before: "Spreadsheets", after: "Real-time", label: "Inventory Sync" },
    { before: "4 tools", after: "1 dashboard", label: "Sales Stack" },
  ];

  return (
    <section id="case-studies" className="py-16 bg-neutral-950 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 mb-8">
        <p className="text-xs uppercase tracking-widest text-gray-600 text-center">Results</p>
      </div>
      
      {/* Scrolling Before → After */}
      <div className="relative">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-neutral-950 to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-neutral-950 to-transparent z-10 pointer-events-none"></div>
        
        <div className="flex animate-scroll gap-6 py-4">
          {[...transformations, ...transformations, ...transformations].map((item, i) => (
            <div
              key={i}
              className="flex-shrink-0 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl px-6 py-5 min-w-[240px] hover:border-red-500/30 transition-all"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-gray-500 line-through text-lg">{item.before}</span>
                <ArrowRight className="w-4 h-4 text-red-400" />
                <span className="text-white font-bold text-xl">{item.after}</span>
              </div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Calculator
const Calculator = () => {
  const [teamSize, setTeamSize] = useState(15);
  const [toolCount, setToolCount] = useState(6);
  const [monthlySpend, setMonthlySpend] = useState(5000);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  const calculate = () => {
    const annual = monthlySpend * 12;
    // More tools = more overlap/waste (35-50% range)
    const wasteRate = Math.min(0.35 + (toolCount * 0.015), 0.50);
    const waste = Math.round(annual * wasteRate);
    // Build cost factors in complexity (tools) and scale (team size)
    const baseCost = 25000;
    const perToolCost = 5000;
    const teamMultiplier = teamSize > 50 ? 1.3 : teamSize > 25 ? 1.15 : 1;
    const buildCost = Math.round((baseCost + (toolCount * perToolCost)) * teamMultiplier);
    const yearOne = waste - buildCost;
    const fiveYear = (annual * 5) - buildCost;
    
    setResults({ monthlySpend, annual, wasteRate: Math.round(wasteRate * 100), waste, buildCost, yearOne, fiveYear });
    setShowResults(true);
  };

  return (
    <section id="calculator" className="py-24 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-red-100 text-red-600 text-sm font-medium rounded-full mb-4">Savings Calculator</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How much are you overpaying?
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            See how much you could save by replacing fragmented SaaS tools with a unified custom solution.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 border border-gray-200 rounded-2xl p-8">
            <h3 className="font-display font-semibold text-gray-900 mb-8">Your current setup</h3>
            
            {/* Monthly SaaS Spend */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm text-gray-600 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  Total monthly SaaS spend
                </label>
                <span className="font-display text-xl font-bold text-gray-900">
                  ${monthlySpend.toLocaleString()}/mo
                </span>
              </div>
              <input
                type="range"
                min={500}
                max={50000}
                step={500}
                value={monthlySpend}
                onChange={(e) => { setMonthlySpend(Number(e.target.value)); setShowResults(false); }}
                className="w-full"
                data-testid="slider-spend"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>$500</span>
                <span>$50,000</span>
              </div>
            </div>

            {/* Number of Tools */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm text-gray-600 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-gray-400" />
                  Number of SaaS tools
                </label>
                <span className="font-display text-xl font-bold text-gray-900">
                  {toolCount} tools
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={20}
                value={toolCount}
                onChange={(e) => { setToolCount(Number(e.target.value)); setShowResults(false); }}
                className="w-full"
                data-testid="slider-tools"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1 tool</span>
                <span>20 tools</span>
              </div>
            </div>
            
            {/* Team Size */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm text-gray-600 flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  Team size
                </label>
                <span className="font-display text-xl font-bold text-gray-900">
                  {teamSize} people
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={100}
                value={teamSize}
                onChange={(e) => { setTeamSize(Number(e.target.value)); setShowResults(false); }}
                className="w-full"
                data-testid="slider-team"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>100</span>
              </div>
            </div>

            <button
              onClick={calculate}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-4 rounded-xl font-medium hover:from-red-600 hover:to-red-700 transition-all shadow-lg shadow-red-500/20 flex items-center justify-center gap-2"
              data-testid="calculate-btn"
            >
              <Zap className="w-5 h-5" />
              Calculate My Savings
            </button>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 border border-gray-200 rounded-2xl p-8">
            <h3 className="font-display font-semibold text-gray-900 mb-8">Your potential savings</h3>
            
            {!showResults ? (
              <div className="h-80 flex flex-col items-center justify-center text-center bg-white/50 rounded-xl border border-dashed border-gray-300">
                <BarChart3 className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 text-sm max-w-xs">Adjust the sliders and click "Calculate My Savings" to see your personalized results.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-5 bg-white rounded-xl border border-gray-200">
                  <div className="text-sm text-gray-500">Annual SaaS spend</div>
                  <div className="font-display text-2xl font-bold text-gray-900 mt-1">${results.annual.toLocaleString()}</div>
                </div>

                <div className="p-5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-100">
                  <div className="text-sm text-red-600 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    Estimated waste ({results.wasteRate}%)
                  </div>
                  <div className="font-display text-3xl font-bold text-red-500 mt-1">${results.waste.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-1">Unused features, duplicate tools, idle licenses</div>
                </div>

                <div className="p-5 bg-white rounded-xl border border-gray-200">
                  <div className="text-sm text-gray-500">Custom solution (one-time)</div>
                  <div className="font-display text-2xl font-bold text-gray-900 mt-1">${results.buildCost.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-1">You own it forever</div>
                </div>

                <div className={`p-5 rounded-xl border ${results.yearOne > 0 ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-100' : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-100'}`}>
                  <div className={`text-sm flex items-center gap-2 ${results.yearOne > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    <Zap className="w-4 h-4" />
                    First year {results.yearOne > 0 ? 'savings' : 'investment'}
                  </div>
                  <div className={`font-display text-3xl font-bold mt-1 ${results.yearOne > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {results.yearOne > 0 ? '' : '-'}${Math.abs(results.yearOne).toLocaleString()}
                  </div>
                </div>

                <div className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-100">
                  <div className="text-sm text-emerald-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    5-year total savings
                  </div>
                  <div className="font-display text-3xl font-bold text-emerald-600 mt-1">${results.fiveYear.toLocaleString()}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Solutions
const Solutions = () => {
  const solutions = [
    { 
      icon: Zap, 
      title: "Custom Mobile & Web Apps", 
      description: "From idea to App Store. We build beautiful, functional apps for iOS, Android, and web—powered by AI where it matters.",
      aiFeature: "Full-stack development",
      color: "red" 
    },
    { 
      icon: Workflow, 
      title: "Workflow Automation", 
      description: "Automate repetitive tasks across your entire operation. If you can describe it, we can automate it.",
      aiFeature: "End-to-end automation",
      color: "blue" 
    },
    { 
      icon: MessageSquare, 
      title: "AI Chatbots & Assistants", 
      description: "Custom chatbots trained on YOUR data. Handle support, sales, or internal queries 24/7.",
      aiFeature: "Fine-tuned on your data",
      color: "purple" 
    },
    { 
      icon: FileText, 
      title: "Document Processing", 
      description: "Extract data from invoices, contracts, forms—any document. AI that learns your business rules.",
      aiFeature: "99% accuracy extraction",
      color: "emerald" 
    },
    { 
      icon: BarChart3, 
      title: "Dashboards & Analytics", 
      description: "Real-time insights from all your data sources. Ask questions in plain English, get instant answers.",
      aiFeature: "Natural language queries",
      color: "orange" 
    },
    { 
      icon: Target, 
      title: "Custom Business Tools", 
      description: "CRMs, inventory systems, scheduling tools—built exactly for how YOU work, not generic software.",
      aiFeature: "Tailored to your process",
      color: "indigo" 
    }
  ];

  const colorClasses = {
    red: { bg: "bg-red-50", iconBg: "bg-red-100", icon: "text-red-500", hover: "hover:border-red-200", badge: "bg-red-100 text-red-600" },
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", icon: "text-blue-500", hover: "hover:border-blue-200", badge: "bg-blue-100 text-blue-600" },
    purple: { bg: "bg-purple-50", iconBg: "bg-purple-100", icon: "text-purple-500", hover: "hover:border-purple-200", badge: "bg-purple-100 text-purple-600" },
    emerald: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", icon: "text-emerald-500", hover: "hover:border-emerald-200", badge: "bg-emerald-100 text-emerald-600" },
    orange: { bg: "bg-orange-50", iconBg: "bg-orange-100", icon: "text-orange-500", hover: "hover:border-orange-200", badge: "bg-orange-100 text-orange-600" },
    indigo: { bg: "bg-indigo-50", iconBg: "bg-indigo-100", icon: "text-indigo-500", hover: "hover:border-indigo-200", badge: "bg-indigo-100 text-indigo-600" }
  };

  return (
    <section id="solutions" className="py-16 px-6 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-red-100 text-red-600 text-sm font-medium rounded-full mb-4">What We Build</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Apps, automation & AI—built for you
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            From mobile apps to enterprise automation, we build custom solutions that fit exactly how you work.
            <span className="font-semibold text-gray-900"> If you can describe it, we can build it.</span>
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution, i) => {
            const colors = colorClasses[solution.color];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className={`p-6 ${colors.bg} rounded-2xl border border-transparent ${colors.hover} transition-all hover:shadow-lg group`}
                data-testid={`solution-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${colors.iconBg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <solution.icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${colors.badge}`}>
                    {solution.aiFeature}
                  </span>
                </div>
                <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">{solution.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{solution.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Engagement Tiers / Pricing
const EngagementTiers = () => {
  const tiers = [
    {
      name: "The Bolt-On",
      subtitle: "Quick Wins",
      initialFee: "$0 - $1,500",
      monthlyFee: "$199",
      warranty: "30 days",
      description: "Simple AI agents, automated intake, or single-workflow fixes.",
      buildIncludes: ["Discovery & planning", "Design & development", "Testing & deployment", "Team training session"],
      supportIncludes: ["Bug fixes", "Minor tweaks", "Email support (48hr response)"],
      highlight: false,
      icon: Zap
    },
    {
      name: "The Rebuild",
      subtitle: "Core Replacement",
      initialFee: "$3,000 - $12,000",
      monthlyFee: "$399",
      warranty: "60 days",
      description: "Replacing 2-3 core SaaS apps (CRM + Scheduling + Billing).",
      buildIncludes: ["Everything in Bolt-On", "Up to 3 integrated systems", "Data migration", "Custom integrations"],
      supportIncludes: ["Bug fixes & updates", "Feature enhancements", "Priority support (24hr response)"],
      highlight: true,
      icon: Target
    },
    {
      name: "The Full Chassis",
      subtitle: "Complete Overhaul",
      initialFee: "Custom Quote",
      monthlyFee: "$599+",
      warranty: "90 days",
      description: "Fully proprietary operations systems for 20+ employee firms.",
      buildIncludes: ["Everything in Rebuild", "Unlimited workflows", "Enterprise integrations", "Admin dashboard"],
      supportIncludes: ["All bug fixes & updates", "New feature development", "Dedicated slack channel", "Same-day response"],
      highlight: false,
      icon: Shield
    }
  ];

  return (
    <section id="pricing" className="py-24 px-6 bg-neutral-950">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Pricing</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            One-time build. You own the code forever. Optional monthly support.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border transition-all hover:bg-white/10 flex flex-col ${
                tier.highlight 
                  ? 'border-red-500/50' 
                  : 'border-white/10'
              }`}
              data-testid={`tier-${i}`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                tier.highlight ? 'bg-red-500/20' : 'bg-white/10'
              }`}>
                <tier.icon className={`w-6 h-6 ${tier.highlight ? 'text-red-400' : 'text-gray-400'}`} />
              </div>
              
              <div className="text-sm text-gray-500 mb-1">{tier.subtitle}</div>
              <h3 className="font-display text-xl font-bold text-white mb-2">{tier.name}</h3>
              <p className="text-gray-400 text-sm mb-4">{tier.description}</p>
              
              {/* Pricing */}
              <div className="space-y-2 mb-5 p-4 bg-black/30 rounded-xl">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">The Build (Upfront)</div>
                  <div className={`font-display text-2xl font-bold ${tier.highlight ? 'text-red-400' : 'text-white'}`}>
                    {tier.initialFee}
                  </div>
                </div>
                <div className="border-t border-white/10 pt-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">The Fuel (Optional)</div>
                  <div className="font-display text-lg font-semibold text-gray-300">
                    {tier.monthlyFee}<span className="text-sm font-normal text-gray-500">/mo</span>
                  </div>
                </div>
                <div className="pt-1">
                  <span className="inline-block text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                    {tier.warranty} free support included
                  </span>
                </div>
              </div>
              
              {/* Build Includes */}
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">Build Includes</div>
                <ul className="space-y-1.5">
                  {tier.buildIncludes.map((item, j) => (
                    <li key={j} className="text-xs text-gray-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Support Includes */}
              <div className="mb-6">
                <div className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">Monthly Support</div>
                <ul className="space-y-1.5">
                  {tier.supportIncludes.map((item, j) => (
                    <li key={j} className="text-xs text-gray-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 text-blue-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <button 
                onClick={openCalBooking}
                className={`mt-auto w-full py-3 rounded-xl font-medium transition-all ${
                  tier.highlight 
                    ? 'bg-white text-black hover:bg-gray-200' 
                    : 'border border-white/20 text-white hover:bg-white/10'
                }`}
              >
                Get Started
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process - simplified like OverAI
const Process = () => {
  const steps = [
    { 
      num: "01", 
      title: "Connect", 
      description: "Any data source, API, database, or legacy system.",
    },
    { 
      num: "02", 
      title: "Build", 
      description: "Custom solution, iterated with your feedback.",
    },
    { 
      num: "03", 
      title: "Ship", 
      description: "Deploy, train your team, hand over the keys.",
    }
  ];

  return (
    <section id="process" className="py-24 px-6 bg-neutral-950">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-widest text-gray-600 mb-4">How it works</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
            Simple process. Serious results.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="text-6xl font-bold text-red-500/80 mb-4">{step.num}</div>
              <h3 className="font-display text-xl font-bold text-white mb-2">{step.title}</h3>
              <p className="text-gray-500">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About
const About = () => (
  <section id="about" className="py-24 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-block px-4 py-1.5 bg-red-500/20 text-red-400 text-sm font-medium rounded-full mb-4">About Redline AI</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Built for businesses ready to own their technology
          </h2>
          <p className="text-gray-400 mb-6 leading-relaxed">
            We help companies break free from expensive SaaS subscriptions by building AI-powered solutions they own completely. No per-seat licensing. No annual price hikes. No vendor lock-in.
          </p>
          <p className="text-gray-400 mb-8 leading-relaxed">
            Every solution integrates with your existing systems, scales with your business, and delivers measurable ROI within the first year.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-5 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="font-semibold text-white">ROI Guarantee</div>
                <div className="text-sm text-gray-400">If our solution doesn't deliver ROI within 12 months, we'll optimize at no cost.</div>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="font-semibold text-white">Full Ownership</div>
                <div className="text-sm text-gray-400">You own 100% of the code. No licensing fees, ever.</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4"
        >
          {[
            { value: "40%", label: "Avg. cost reduction", color: "from-red-500 to-orange-500" },
            { value: "12mo", label: "Typical ROI", color: "from-blue-500 to-indigo-500" },
            { value: "100%", label: "Code ownership", color: "from-emerald-500 to-teal-500" },
            { value: "$0", label: "Recurring fees", color: "from-purple-500 to-pink-500" }
          ].map((stat, i) => (
            <div key={i} className="text-center p-8 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
              <div className={`font-display text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>{stat.value}</div>
              <div className="text-sm text-gray-400 mt-2">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

// Contact
const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!form.name || !form.email || !form.message) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ type: 'success', message: data.message || 'Message sent successfully!' });
        setForm({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus({ type: 'error', message: data.detail || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-black">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Ready to build?</p>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
          Let's talk.
        </h2>
        <p className="text-xl text-gray-400 mb-10">
          30 minutes. No pitch deck. Just a conversation about what you're trying to build.
        </p>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={openCalBooking}
          className="bg-white text-black px-10 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all inline-flex items-center gap-2"
          data-testid="calendly-button"
        >
          Book a Call
          <ArrowRight className="w-5 h-5" />
        </motion.button>
        
        <p className="mt-8 text-gray-600 text-sm">
          Or email us at{' '}
          <a href="mailto:mike@redlinemonitoring.com" className="text-gray-400 hover:text-white transition-colors">
            mike@redlinemonitoring.com
          </a>
        </p>
      </div>
    </section>
  );
};

// Footer - minimal with links
const Footer = () => {
  return (
    <footer className="py-8 px-6 bg-black border-t border-white/10">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M50 5L93.3 27.5V72.5L50 95L6.7 72.5V27.5L50 5Z" fill="#DC2626"/>
              <path d="M35 35H50C55.5 35 60 39.5 60 45C60 50.5 55.5 55 50 55H42V65H35V35ZM42 48H48C50.2 48 52 46.2 52 44C52 41.8 50.2 40 48 40H42V48Z" fill="white"/>
              <path d="M50 55L65 65H55L42 55H50Z" fill="white"/>
            </svg>
            <span className="font-display font-bold text-white">Build with Redline</span>
          </div>
          
          <div className="flex items-center gap-6">
            <a 
              href="mailto:mike@redlinemonitoring.com" 
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              mike@redlinemonitoring.com
            </a>
            <a 
              href="https://linkedin.com/company/buildwithredline" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-white/10">
          <div className="text-sm text-gray-500">
            © 2026 Build with Redline
          </div>
          <Link 
            to="/privacy" 
            className="text-sm text-gray-500 hover:text-gray-400 transition-colors"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  );
};

// Home page component
const HomePage = () => (
  <div className="min-h-screen bg-black">
    <Navigation />
    <Hero />
    <MissionBriefing />
    <Process />
    <CaseStudies />
    <Founders />
    <Contact />
    <Footer />
    <BackToTop />
  </div>
);

function App() {
  usePageTracking();
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
}

export default App;
